// src/utils/obbligoPresenze.js
// ═══════════════════════════════════════════════════════════════════════════
// CARTELLINO OBBLIGATORIO — «prima metti in regola ieri» (ottobre 2026, W419, regole 66)
// ═══════════════════════════════════════════════════════════════════════════
// Deciso con Paolo: la timbratura dimenticata non si punisce togliendo informazioni (il
// programma serve per lavorare), si chiede il dato prima di andare avanti. Ogni azienda
// sceglie il livello in Impostazioni → Presenze e approvazioni:
//   spento  — niente;
//   avvisi  — solo notifiche (sera: uscita mancante; mattina: elenco a chi approva);
//   normale — all'apertura dell'app i giorni mancanti: «Inserisco gli orari» o «Giustifica»;
//             fatto uno dei due, l'app si usa normalmente;
//   severa  — come normale, ma con la giustificazione si sblocca solo quando è accettata;
//             nell'attesa resta sempre visibile «Oggi sei a: …» e il cartellino.
//
// Quali giorni contano (stessa regola nell'app, nella Home di chi approva e nella Cloud
// Function della sera — functions/obbligoPresenzeCF.js):
//   • si guarda indietro al massimo GIORNI_INDIETRO giorni, mai prima dell'attivazione
//     (obbligoPresenzeDal), dell'assunzione o dopo la cessazione; oggi non conta mai;
//   • chi è escluso dalla timbratura (Presenze → esclusioni) non ha giorni;
//   • se quel giorno la persona era in programmazione su lavoro (commessa, Magazzino,
//     Ufficio, Sopralluogo, Commerciale) doveva timbrare, anche di sabato o di festa;
//   • in programmazione Ferie, Permesso o Malattia: niente;
//   • se l'azienda quella settimana non ha programmato nessuno, valgono i giorni feriali
//     (lunedì–venerdì, festività nazionali escluse);
//   • manca: nessuna timbratura, oppure un'entrata di lavoro senza uscita. Ferie, permessi
//     e malattie registrati sono timbrature speciali: il giorno è già a posto.
// ═══════════════════════════════════════════════════════════════════════════
import { eFeriale, dataStr, parseData } from "./periodoLavorativo";
import { segmentiGiornata } from "./segmentiGiornata";
import { etichettaMezzo } from "./mezziProgrammazione";

export const GIORNI_INDIETRO = 7;

export const LIVELLI_OBBLIGO = [
  { id: "spento", label: "Spento", desc: "Nessun avviso e nessun blocco." },
  { id: "avvisi", label: "Solo avvisi", desc: "La sera chi ha l'entrata senza uscita riceve una notifica; la mattina chi approva i cartellini vede in Home chi ieri non ha timbrato." },
  { id: "normale", label: "Compila o giustifica (consigliato)", desc: "Gli avvisi, e in più chi ha un giorno mancante, aprendo Nexiora, prima inserisce gli orari o scrive una giustificazione. Fatto uno dei due, usa l'app normalmente." },
  { id: "severa", label: "Severa", desc: "Come «Compila o giustifica», ma con la giustificazione l'app si sblocca solo quando chi approva i cartellini la accetta. Nell'attesa la persona vede sempre dove lavora oggi e il suo cartellino." },
];
const IDS = LIVELLI_OBBLIGO.map(l => l.id);

/** Livello scelto dall'azienda (assente = spento: alle aziende già attive non cambia nulla). */
export const livelloObbligo = (org) => (IDS.includes(org?.obbligoPresenze) ? org.obbligoPresenze : "spento");
/** Il livello prevede la schermata all'apertura? */
export const conSchermata = (livello) => livello === "normale" || livello === "severa";

/** Voci della programmazione che non sono lavoro. */
export const ASSENZE_PROGRAMMAZIONE = ["Ferie", "Permesso", "Malattia"];

/** Id del documento del cartellino (mese senza zero, come in Presenze). */
export const idCartellino = (orgId, uid, d) => `${orgId}_${uid}_${d.getFullYear()}_${d.getMonth() + 1}`;
/** Lunedì della settimana di d. */
export function lunediDi(d) {
  const l = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  l.setDate(l.getDate() - ((l.getDay() + 6) % 7));
  return l;
}
/** Id del documento della programmazione settimanale (stesso formato di ProgrammazioneSettimanalePage). */
export const idProgrammazione = (orgId, d) => { const l = lunediDi(d); return `${orgId}_${dataStr(l).replace(/-/g, "")}`; };

/** Celle di una programmazione settimanale → [{ uid, data, commessaNome, commessaId, mezzo }]. */
export function celleDaProgrammazione(docProg) {
  const out = [];
  Object.entries(docProg?.celle || {}).forEach(([chiave, voce]) => {
    const data = (chiave.match(/(\d{4}-\d{2}-\d{2})$/) || [])[1] || "";
    (Array.isArray(voce) ? voce : voce && typeof voce === "object" ? [voce] : []).forEach(v => {
      const nome = String(v.commessaNome || "").trim();
      if (!data || !nome) return;
      out.push({ uid: v.uid || chiave.split("_")[0], data, commessaNome: nome, commessaId: v.commessaId || "", mezzo: etichettaMezzo(v) });
    });
  });
  return out;
}

/** La giornata del cartellino è a posto? → null, "nessuna" o "uscita". */
export function mancanzaGiornata(giorno) {
  const timbr = Array.isArray(giorno?.timbrature) ? giorno.timbrature : [];
  if (!timbr.length) return "nessuna";
  return segmentiGiornata(timbr).apertaLavoro ? "uscita" : null;
}

/** L'operaio può ancora scrivere da sé quel giorno? (stessa finestra del cartellino: mese corrente, fino al giorno dopo) */
export function giornoCorreggibileDaSe(d, oggi = new Date()) {
  if (d.getFullYear() !== oggi.getFullYear() || d.getMonth() !== oggi.getMonth()) return false;
  return oggi < new Date(d.getFullYear(), d.getMonth(), d.getDate() + 2);
}

/** La persona doveva timbrare quel giorno? (celle = tutte le celle delle settimane coinvolte) */
export function dovevaTimbrare(d, uid, celle) {
  const ds = dataStr(d);
  const mie = celle.filter(c => c.uid === uid && c.data === ds);
  if (mie.some(c => !ASSENZE_PROGRAMMAZIONE.includes(c.commessaNome))) return true;
  if (mie.length) return false;                                  // solo Ferie/Permesso/Malattia
  if (!eFeriale(d)) return false;
  const lun = dataStr(lunediDi(d)), dom = new Date(lunediDi(d)); dom.setDate(dom.getDate() + 6);
  const domS = dataStr(dom);
  const settimanaProgrammata = celle.some(c => c.data >= lun && c.data <= domS);
  return !settimanaProgrammata;
}

/**
 * Giorni da mettere in regola, dal più vecchio.
 * @param {object} p
 *   oggi, livello, dal ("YYYY-MM-DD" attivazione), periodo ({soggetto, inizio, fine}),
 *   cartellini: { idCartellino → giorni } (solo i mesi coinvolti), celle, giustificazioni
 *   (della persona), uid, orgId
 * @returns [{ data, giorno: Date, mancanza, correggibile, giustificazione, stato }]
 *   stato: "da_fare" | "respinta" | "in_attesa" (severa, giustificazione inviata) | "a_posto"
 */
export function giorniDaSistemare({ oggi = new Date(), livello = "normale", dal = "", periodo = null, cartellini = {}, celle = [], giustificazioni = [], uid, orgId }) {
  if (!uid || !orgId || livello === "spento") return [];
  if (periodo && periodo.soggetto === false) return [];
  const inizioAttivazione = parseData(dal);
  const out = [];
  for (let n = GIORNI_INDIETRO; n >= 1; n--) {
    const d = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - n);
    if (inizioAttivazione && d < inizioAttivazione) continue;
    if (periodo?.inizio && d < periodo.inizio) continue;
    if (periodo?.fine && d > periodo.fine) continue;
    if (!dovevaTimbrare(d, uid, celle)) continue;
    const giorni = cartellini[idCartellino(orgId, uid, d)] || {};
    const mancanza = mancanzaGiornata(giorni[String(d.getDate())]);
    if (!mancanza) continue;
    const ds = dataStr(d);
    const g = giustificazioni.filter(x => x.data === ds)
      .sort((a, b) => (tempo(b.createdAt) - tempo(a.createdAt)))[0] || null;
    let stato = "da_fare";
    if (g?.stato === "accettata") stato = "a_posto";
    else if (g?.stato === "respinta") stato = "respinta";
    else if (g?.stato === "inviata") stato = livello === "severa" ? "in_attesa" : "a_posto";
    out.push({ data: ds, giorno: d, mancanza, correggibile: giornoCorreggibileDaSe(d, oggi), giustificazione: g, stato });
  }
  return out;
}
const tempo = (v) => (v?.toMillis ? v.toMillis() : v?.seconds ? v.seconds * 1000 : (typeof v === "number" ? v : 0));

/** L'app va fermata? (livelli con schermata e almeno un giorno non a posto) */
export function bloccante(livello, giorni) {
  return conSchermata(livello) && giorni.some(g => g.stato !== "a_posto");
}

/** «giovedì 22 ottobre» */
export const dataLunga = (d) => d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
/** «Giovedì 22 ottobre» (inizio di frase: solo la prima lettera maiuscola) */
export const dataLungaInizio = (d) => { const t = dataLunga(d); return t.charAt(0).toUpperCase() + t.slice(1); };

export const TESTO_MANCANZA = { nessuna: "nessuna timbratura", uscita: "manca l'uscita" };
